import type {
  IAdCalculatedResults,
  TAdInParams
} from '@web/features/kinematic-form/model/ad-form.type'
import type { TBdInParams } from '@web/features/kinematic-form/model/bd-form.type'
import type { TBrInParams } from '@web/features/kinematic-form/model/br-form.type'
import type { TCdInParams } from '@web/features/kinematic-form/model/cd-form.type'
import type { TDcgInParams } from '@web/features/kinematic-form/model/dcg-form.type'
import type { TDcmInParams } from '@web/features/kinematic-form/model/dcm-form.type'
import type { TGbInParams } from '@web/features/kinematic-form/model/gb-form.type'
import type { TGbdInParams } from '@web/features/kinematic-form/model/gb-type-form.type'
import type { TSdInParams } from '@web/features/kinematic-form/model/sd-form.type'
import type { TSgInParams } from '@web/features/kinematic-form/model/sg-form.type'
import type { TTgbInParams } from '@web/features/kinematic-form/model/tgb-form.type'
import type { TTnInParams } from '@web/features/kinematic-form/model/tn-cn-form.type'
import type { TWheelTypeInParams } from '@web/features/kinematic-form/model/wheel-type-form.type'
import type { TId } from '@web/shared/types/id.type'

export enum EKinematicBlockType {
  BR = 'BR',
  BF = 'BF',
  BS = 'BS',
  AD = 'AD',
  SD = 'SD',
  SG = 'SG',
  DCM = 'DCM',
  DCG = 'DCG',
  GB = 'GB',
  GBD = 'GBD',
  GBT = 'GBT',
  TGB = 'TGB',
  TW = 'TW',
  CW = 'CW',
  BD = 'BD',
  CD = 'CD',
  TN = 'TN',
  CN = 'CN',
  CP = 'CP',
  SH = 'SH',
  HD = 'HD',
  PD = 'PD'
}

export enum FreqRegul {
  None = 'NONE',
  PhaseRotor = 'PHASE_ROTOR',
  PWM = 'PWM'
}

export interface IOut {
  freqOutName?: string
  freqOutCoefKb?: number
  freqOutCoefMachine?: number
  freqOutValue?: number
}

export interface IIn {
  freqInName?: string
  freqCoefForIn?: number
  kinematicBlockSourceId?: string
  pointSourceId?: string
  measurementSourceId?: string
  freqInValue?: number
  freqInCoefMachine?: number
}

export interface IKinematicBlockBR {
  n?: number
  alfa?: number
  bearingName?: string
  manufacturerName?: string
  kftfo?: number
  kbsf?: number
  kbpfo?: number
  kbpfi?: number
  din?: number
  dout?: number
  dtk?: number
  kftf?: number
}

export interface IKinematicBlockAD {
  n2p?: number
  freqRegul: FreqRegul
  pwmData?: boolean
  fu?: number
  nr?: number
  ns?: number
  fsh?: number
  fumin?: number
  fumax?: number
}

export interface IKinematicBlockSD {
  n2p?: number
  fu?: number
  nr?: number
  ns?: number
}

export interface IKinematicBlockSG {
  n2p?: number
  fu?: number
  nr?: number
  ns?: number
}

export interface IKinematicBlockDCM {
  n2p?: number
  nc?: number
  fu?: number
  nr?: number
}

export interface IKinematicBlockDCG {
  n2p?: number
  nc?: number
  fu?: number
  nr?: number
}

export interface IKinematicBlockGB {
  z1?: number
  z2?: number
}

export interface IKinematicBlockGBD {
  z1?: number
  z2?: number
  z3?: number
}

export interface IKinematicBlockGBT {
  z1?: number
  z2?: number
  z3?: number
}

export interface IKinematicBlockTGB {
  z1?: number
  z2?: number
  z3?: number
}

export interface IKinematicBlockTW {
  zb?: number
}

export interface IKinematicBlockCW {
  zb?: number
}

export interface IKinematicBlockBD {
  d1?: number
  d2?: number
  l?: number
}

export interface IKinematicBlockCD {
  z1?: number
  z2?: number
  z3?: number
}

export interface IKinematicBlockTN {
  nst?: number
  zbi?: number[]
}

export interface IKinematicBlockCN {
  nst?: number
  zbi?: number[]
}

export interface IKinematicElement {
  kinematicBlockId: TId
  kinematicSchemeId: TId
  kinematicBlockName: string
  kinematicBlockType: EKinematicBlockType
  description?: string

  imageSourceKS?: string
  imageSourceCW?: string

  width: number
  height: number
  x: number
  y: number

  measurementPointIds?: string[]

  ins?: IIn[]
  outs: IOut[]

  kinematicBlockBR: IKinematicBlockBR | null
  kinematicBlockAD: IKinematicBlockAD | null
  kinematicBlockSD: IKinematicBlockSD | null
  kinematicBlockSG: IKinematicBlockSG | null
  kinematicBlockDCM: IKinematicBlockDCM | null
  kinematicBlockDCG: IKinematicBlockDCG | null
  kinematicBlockGB: IKinematicBlockGB | null
  kinematicBlockGBD: IKinematicBlockGBD | null
  kinematicBlockGBT: IKinematicBlockGBT | null
  kinematicBlockTGB: IKinematicBlockTGB | null
  kinematicBlockTW: IKinematicBlockTW | null
  kinematicBlockCW: IKinematicBlockCW | null
  kinematicBlockBD: IKinematicBlockBD | null
  kinematicBlockCD: IKinematicBlockCD | null
  kinematicBlockTN: IKinematicBlockTN | null
  kinematicBlockCN: IKinematicBlockCN | null

  connectedKinematicIds?: string[]
}

export type TKinematicToChangeDb = Omit<
  IKinematicElement,
  'x' | 'y' | 'width' | 'height'
>

export type TCreateKinematicElementBody = Omit<
  IKinematicElement,
  'kinematicSchemeId' | 'kinematicBlockId'
>

export interface IKinematicScheme {
  kinematicSchemeId?: string
  nodeId: string
  name: string
  width: number
  height: number
}

export interface ICreateKinematicSchemeResponse {
  id: string
}

export interface IGetKinematicSchemeElementsResponse {
  content: IKinematicElement[]
  totalElements: number
}

export interface IDefinitionRemoveCurrentKinematicElement {
  kinematicSchemeId: TId
  kinematicElementIds: TId[]
}

export type TCalculateKinematicCascade = {
  kinematicBlockId: TId
  type?: EKinematicBlockType
  freqInName?: string
  freqInName2?: string
  freqCoefForIn?: number
  kinematicBlockSourceId?: string
  measurementSourceId?: string
} & (
  | TAdInParams
  | TBdInParams
  | TBrInParams
  | TSdInParams
  | TSgInParams
  | TCdInParams
  | TDcgInParams
  | TDcmInParams
  | TGbInParams
  | TGbdInParams
  | TTgbInParams
  | TWheelTypeInParams
  | TTnInParams
)

export type TCalculatedResults = IAdCalculatedResults

export interface IAttributesKinematicBlocks {
  type: EKinematicBlockType
  title: string
  iconSrc: string
  iconSrcDark: string
  width: number
  height: number
}
